import {BaseDomainService} from "../../../infrastructure/domain-services/base-domain-service";
import {EntitiesRepository} from "./entities.repository";
import {EntitiesFactory} from "./entities.factory";
import {CompetitionsDomainService} from "../../competitions/services/competitions.domain-service";
import {CompetitorsDomainService} from "../../competitors/services/competitors.domain-service";
import {EntitiesResponseDomainModel} from "../models/entities.response-domain-model";
import {CompetitionsResponseDomainModel} from "../../competitions/models/competitions.response-domain-model";
import {CompetitionsRequestParams} from "../../competitions/models/competitions.request-params";
import {CompetitorsRequestParams} from "../../competitors/models/competitors.request-params";
import {CompetitorsResponseDomainModel} from "../../competitors/models/competitors.response-domain-model";
import {CompetitorDomainModel} from "../../common/competitor/models/competitor-domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {BaseDomainParams} from "../../../infrastructure/models/params/base-domain-params";
import {PartialGameRequestParams} from "../../partial-game/models/partial-game.request-params";
import {PartialGameDomainService} from "../../partial-game/services/partial-game.domain.service";
import {PartialGameDataSource} from "../../partial-game/models/partial-game-data-source";
import {PartialGameDomainModel} from "../../partial-game/models/partial-game-domain-model";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {BaseManagedService} from "../../../infrastructure/domain-services/base-managed-service";
import {DomainRequestManager} from "../../../core/managers/domain-request-manager";
import {DomUtils} from "../../../infrastructure/utils/dom-utils";
import {SiteConfig} from "../../../core/bootstrap/models/site-config";
import {DeviceStore} from "../../../core/app/device.store";
import {AppStore} from "../../../core/app/app-store";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {HistoryManager} from "../../../core/managers/history-manager";
import {LocalStorageUtils} from "../../../infrastructure/utils/local-storage.utils";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {COMPETITION_CATEGORY} from "../../../core/app/consts";
import {FullAthletesRequestParams} from "../../full-athlete/full-athletes.request-params";
import {AthletesDomainService} from "../../athletes/services/athletes.domain-service";
import {AthletesDataSource} from "../../athletes/models/athletes-data.source";
import {AthleteDomainModel} from "../../common/athlete/athlete-domain-model";

export class EntitiesDomainService extends BaseDomainService {
  competitionsDomainService: CompetitionsDomainService;
  competitorsDomainService: CompetitorsDomainService;
  partialGameDomainService: PartialGameDomainService;
  athleteDomainService: AthletesDomainService;

  static injectables = [...BaseManagedService.injectables, EntitiesRepository, EntitiesFactory, DomainRequestManager, CompetitionsDomainService, CompetitorsDomainService, PartialGameDomainService, AthletesDomainService];

  constructor(
    domUtils: DomUtils,
    logger: LoggerUtils,
    siteConfig: SiteConfig,
    deviceStore: DeviceStore,
    appStore: AppStore,
    navigationManager: NavigationManager,
    historyManager: HistoryManager,
    localStorageUtils: LocalStorageUtils,
    repository: EntitiesRepository,
    factory: EntitiesFactory,
    requestManager: DomainRequestManager,
    competitionsDomainService: CompetitionsDomainService,
    competitorsDomainService: CompetitorsDomainService,
    partialGameDomainService: PartialGameDomainService,
    athleteDomainService: AthletesDomainService,
  ) {
    super(
      domUtils,
      logger,
      siteConfig,
      deviceStore,
      appStore,
      navigationManager,
      historyManager,
      localStorageUtils,
      repository,
      factory,
      requestManager,
    );

    contractsUtils.requiredType(competitionsDomainService, CompetitionsDomainService);
    contractsUtils.requiredType(competitorsDomainService, CompetitorsDomainService);
    contractsUtils.requiredType(partialGameDomainService, PartialGameDomainService);
    contractsUtils.requiredType(athleteDomainService, AthletesDomainService);

    this.competitionsDomainService = competitionsDomainService;
    this.competitorsDomainService = competitorsDomainService;
    this.partialGameDomainService = partialGameDomainService;
    this.athleteDomainService = athleteDomainService;
  }

  async fetch(params: BaseDomainParams, force = false, isDelta = false): Promise<EntitiesResponseDomainModel> {
    return Promise.all([this.fetchCompetitions(params), this.fetchCompetitors(params), this.fetchGames(params), this.fetchAthletes(params)]).then((values) => {
      const competitions: Array<CompetitionDomainModel> = values[0] ? values[0].competitions : [];
      const competitors: Array<CompetitorDomainModel> = values[1] ? values[1].competitors : [];
      const games: Array<PartialGameDomainModel> = values[2] ? values[2].games : [];
      const athletes: Array<AthleteDomainModel> = values[3] ? values[3].athletes : [];
      const request = this.repository.api._createRequest(params.requestParams);
      const response: EntitiesResponseDomainModel = this.factory.createDomainModel(competitions, competitors, games, athletes, request);
      request.response = response;
      request.promise = Promise.resolve(response);

      return request.promise;
    })

  }

  async fetchCompetitors(params: BaseDomainParams): Promise<CompetitorsResponseDomainModel> {
    if (!params.requestParams.competitors || !params.requestParams.competitors.length) {
      return null;
    }

    const competitors: Array<Number> = params.requestParams.competitors;
    const withSeason: Boolean = params.requestParams.withSeasons;

    const competitorsRequestParams: CompetitorsRequestParams = new CompetitorsRequestParams(null, competitors, null, null, null, withSeason);
    competitorsRequestParams.initialParams = this.createInitialRequestParams();
    const competitorsDomainParams: BaseDomainParams = new BaseDomainParams(competitorsRequestParams);
    const competitorsResponse: CompetitorsResponseDomainModel = await this.competitorsDomainService.fetch(competitorsDomainParams);

    return competitorsResponse;
  }

  async fetchCompetitions(params: BaseDomainParams): Promise<CompetitionsResponseDomainModel> {
    if (!params.requestParams.competitions || !params.requestParams.competitions.length) {
      return null;
    }

    const competitions: Array<Number> = params.requestParams.competitions;
    const withSeason: Boolean = params.requestParams.withSeasons;
    const isDashboardRequest: Boolean = competitions?.length === 1 && withSeason;
    const competitionsRequestParams: CompetitionsRequestParams = new CompetitionsRequestParams(null, competitions, COMPETITION_CATEGORY.default, null, null, withSeason, true, isDashboardRequest);
    competitionsRequestParams.initialParams = this.createInitialRequestParams();
    const competitionsDomainParams: BaseDomainParams = new BaseDomainParams(competitionsRequestParams);
    const competitionsResponse: CompetitionsResponseDomainModel = await this.competitionsDomainService.fetch(competitionsDomainParams);

    return competitionsResponse;
  }

  async fetchGames(params: BaseDomainParams): Promise<CompetitionsResponseDomainModel> {
    if (!params.requestParams.games || !params.requestParams.games.length) {
      return null;
    }

    const gamesRequestParams: PartialGameRequestParams = new PartialGameRequestParams(params.requestParams.games);
    gamesRequestParams.initialParams = this.createInitialRequestParams();
    const gamesDomainParams: BaseDomainParams = new BaseDomainParams(gamesRequestParams);
    const gamesResponse: PartialGameDataSource = await this.partialGameDomainService.fetch(gamesDomainParams);

    return gamesResponse;
  }

  async fetchAthletes(params: BaseDomainParams): Promise<AthletesDataSource> {
    if (!params.requestParams.athletes || !params.requestParams.athletes.length) {
      return null;
    }
    const athletes: Array<Number> = params.requestParams.athletes;

    const athletesRequestParams: FullAthletesRequestParams = new FullAthletesRequestParams(athletes, -1, '', null, false);
    athletesRequestParams.initialParams = this.createInitialRequestParams();
    const athletesDomainParams: BaseDomainParams = new BaseDomainParams(athletesRequestParams);
    const athletesResponse: AthletesDataSource = await this.athleteDomainService.fetch(athletesDomainParams);

    return athletesResponse;
  }

}
